/**
 * The sections the mobile bar has no room for.
 *
 * The bar carries four destinations and this one. A fifth would push the
 * account's own figures off the row, and a section reachable only on a desktop
 * is a section that does not exist for anybody holding a phone.
 *
 * It lists what the shell already knows: the same section list the top bar
 * reads, minus what the bar itself carries. A second hand-written list here
 * would drift, and the copy that drifted would be the one deciding what a
 * person can reach.
 *
 * Sections that are not built yet are listed and marked, not hidden. A section
 * a person cannot see is a section they assume does not exist, and then ask
 * for again.
 */
import { ChevronRight } from "lucide-react";
import { NavLink } from "react-router";


import { sectionsFor } from "./navigation";

export function MorePage({ roles = [] }: { readonly roles?: readonly string[] }): React.JSX.Element {
  // Everything the bottom bar does not already carry, and never the overview
  // itself: it is the bar's first destination.
  const rest = sectionsFor(roles).filter((section) => !section.onMobile && section.path !== "/app");

  return (
    <div className="flex min-w-0 flex-col gap-3" data-testid="more-page">
      <h1 className="sr-only">Ещё</h1>
      <nav aria-label="Другие разделы">
        <ul className="flex flex-col overflow-hidden rounded-panel border border-border-subtle bg-surface-default">
          {rest.map((section) => {
            const Icon = section.icon;
            const row = (
              <>
                <Icon aria-hidden="true" className="size-4 shrink-0 text-text-muted" />
                <span className="min-w-0 flex-1 text-body-main">
                  {section.label}
                </span>
                {section.available ? (
                  <ChevronRight
                    aria-hidden="true"
                    className="size-4 shrink-0 text-text-muted"
                  />
                ) : (
                  <span className="text-helper-sm text-text-muted">скоро</span>
                )}
              </>
            );
            return (
              <li
                key={section.path}
                className="border-b border-border-subtle last:border-b-0"
              >
                {section.available ? (
                  <NavLink
                    to={section.path}
                    data-testid={`more-${section.path}`}
                    className="flex min-h-11 cursor-pointer items-center gap-3 px-3 text-text-primary transition-colors duration-150 hover:bg-surface-hover focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-focus-ring"
                  >
                    {row}
                  </NavLink>
                ) : (
                  <span
                    data-testid={`more-${section.path}`}
                    className="flex min-h-11 items-center gap-3 px-3 text-text-muted"
                  >
                    {row}
                  </span>
                )}
              </li>
            );
          })}
        </ul>
      </nav>
    </div>
  );
}
